import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { compact, isObject } from 'lodash-es';
import { Divider, InlineSelect, MenuItem, Tooltip, Typography, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { billingInitiationTextsPath, billingStagesTextsPath, billingTriggersTextsPath, makeInitialOneTimeInStages, serviceBillingStagesTermsOptions, serviceBillingTriggerOptions } from '~/business-logic/billingLogic';
import { TextAndInlineSelectRow } from '~/components/ServiceAdvancedSettings/TextAndInlineSelectRow';
import { BillingStagesSelect } from '~/components/ServiceBuilder/BillingStagesSelect/BillingStagesSelect';
import { recurrencePeriodsTextsPath } from '~/constants';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { OneTimeInStagesError, validateOneTimeInStages } from '~/models/service/logic';
import { isAwaitingApproval, isFromAmendment } from '~/models/service/selectors';
import { makeBillUpfrontState } from '~/models/service-billing';
import { PreapprovalPeriodType, RecurrencePeriodTypes } from '~/models/service-billing/model';
import { price } from '~/models/service-cost';
import { InlineUpfrontSelect } from './InlineUpfrontSelect/InlineUpfrontSelect';
export const GetBillingInitiationControlTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            BillingInitiationControlTestIds: {
                Root: "".concat(parentTestId, "-billing-initiation-control"),
                Occurrence: {
                    Root: "".concat(parentTestId, "-occurrence"),
                    Tooltip: "".concat(parentTestId, "-occurrence-tooltip"),
                    Option: (value)=>"".concat(parentTestId, "-occurrence-option-").concat(value)
                },
                Trigger: {
                    Root: "".concat(parentTestId, "-trigger"),
                    Tooltip: "".concat(parentTestId, "-trigger-tooltip"),
                    Option: (value)=>"".concat(parentTestId, "-trigger-option-").concat(value)
                },
                Terms: {
                    Root: "".concat(parentTestId, "-terms"),
                    Option: (value)=>"".concat(parentTestId, "-terms-option-").concat(value)
                }
            }
        }
    });
function serviceBillingOccurrenceOptions(param) {
    let { isVariablePrice, onDemandEnabled, isCurrentlyOnDemand, recurrencePeriodsTexts } = param;
    return compact([
        (onDemandEnabled || isCurrentlyOnDemand) && {
            value: 'onDemand',
            disabled: !isVariablePrice
        },
        {
            value: 'onetime'
        },
        {
            value: 'inStages'
        },
        {
            value: RecurrencePeriodTypes.Weekly
        },
        {
            value: RecurrencePeriodTypes.BiWeekly
        },
        {
            value: RecurrencePeriodTypes.Monthly
        },
        {
            value: RecurrencePeriodTypes.Quarterly
        },
        {
            value: RecurrencePeriodTypes.Yearly
        }
    ]).map((param)=>{
        let { value, disabled } = param;
        return {
            value,
            text: serviceBillingModel.extendedRecurrencePeriodText(value, false, recurrencePeriodsTexts),
            disabled
        };
    });
}
function getOccurrenceLabel(occurrence, t, recurrencePeriodsTexts) {
    if (!occurrence) {
        return t.labels.once();
    }
    return serviceBillingModel.extendedRecurrencePeriodText(occurrence, true, recurrencePeriodsTexts).toLowerCase();
}
function getTriggerLabel(option, t) {
    const autoLabel = t.labels.automatically();
    const manualLabel = t.labels.manually();
    const optionLabelMap = {
        approval: autoLabel,
        ongoing: autoLabel,
        manualVariable: manualLabel,
        manualFixed: manualLabel,
        oneTimeInStages: t.labels.inStages(),
        onDemand: manualLabel
    };
    return optionLabelMap[option];
}
function renderMenuItem(param) {
    let { value, text, subtext, testId, disabled, tooltipProps, isMobileDevice } = param;
    return /*#__PURE__*/ _jsxs(MenuItem, {
        value: value,
        variant: "multiline",
        testId: testId,
        style: isMobileDevice ? undefined : {
            maxWidth: 330,
            minWidth: 100
        },
        disabled: disabled,
        tooltipProps: tooltipProps,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                weight: subtext ? 'normal' : 'light',
                children: text
            }),
            subtext && /*#__PURE__*/ _jsx(Typography, {
                children: subtext
            })
        ]
    }, value);
}
function matchOption(option, matcher) {
    switch(option){
        case 'manualFixed':
        case 'manualVariable':
            return matcher.manual();
        case 'oneTimeInStages':
            return matcher.inStages();
        case 'approval':
        case 'ongoing':
            return matcher.auto();
        default:
            {
                const _exhaustive = option;
                throw new Error("Unhandled trigger option: ".concat(_exhaustive));
            }
    }
}
function getOccurrenceOptionTooltip(param) {
    let { isInStagesOption, isOnDemandOption, isDisabled, hasValueDiscount, isFixedPrice, oneTimeInStages, t } = param;
    if (!isDisabled) return undefined;
    if (isInStagesOption && hasValueDiscount && !oneTimeInStages) return t.tooltips.mustBePercentage();
    if (isInStagesOption && !isFixedPrice && !oneTimeInStages) return t.tooltips.inStagesFixedPriceOnly();
    if (isOnDemandOption) return t.tooltips.onDemandVariablePriceOnly();
    return undefined;
}
function getOccurrenceValue(service) {
    const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    if (oneTimeInStages) {
        return 'inStages';
    }
    if (serviceBillingModel.isOnDemand(service.billing)) {
        return 'onDemand';
    }
    if (!recurrencePeriod) {
        return 'onetime';
    }
    return recurrencePeriod;
}
function getTriggerValue(service) {
    const manualOption = ()=>serviceCostModel.isFixedPrice(service.cost) ? 'manualFixed' : 'manualVariable';
    return serviceBillingModel.accept(service.billing, {
        visitOneTimeManual: manualOption,
        visitOnApproval: ()=>'approval',
        visitOneTimeInStages: ()=>'oneTimeInStages',
        visitOngoing: ()=>'ongoing',
        visitRepeatableManual: manualOption,
        visitOnDemand: manualOption
    });
}
export default function BillingInitiationControl(props) {
    var _ref;
    var _service_cost_discount;
    const { testId, service, disabled, agreementData, isExistingService, onChange = ()=>{}, onBillUpfrontModeChange, isReadOnly } = props;
    const testIds = useMemo(()=>GetBillingInitiationControlTID(testId), [
        testId
    ]);
    const { BillingInitiationControlTestIds } = testIds;
    const { isMobileDevice } = useDevice();
    const isDisabled = !!disabled;
    const disabledReason = isObject(disabled) ? disabled.reason : undefined;
    const featureFlags = useFeatureFlags();
    const { ON_DEMAND } = featureFlags;
    const { getNow } = useIoContext();
    const { t: billingTriggersTexts } = useLocaleKeys(billingTriggersTextsPath);
    const { t: billingStagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const { t: billingInitiationTexts } = useLocaleKeys(billingInitiationTextsPath);
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const now = useMemo(()=>getNow(), []);
    const isFromAgreement = agreementData.isActiveAgreement();
    const occurrenceValue = getOccurrenceValue(service);
    const triggerValue = getTriggerValue(service);
    const isOngoing = serviceBillingModel.isOngoing(service.billing);
    const isOnDemand = serviceBillingModel.isOnDemand(service.billing);
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
    const isAutomaticOptionSelected = serviceBillingModel.isAutomatic(service.billing);
    const shouldShowYearlyManualTooltip = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
    const hasNonPercentageDiscountError = oneTimeInStages ? validateOneTimeInStages(service).includes(OneTimeInStagesError.NonPercentageDiscount) : false;
    const discount = serviceModel.discount(service);
    const hasValueDiscount = (_ref = discount === null || discount === void 0 ? void 0 : discount.isValue()) !== null && _ref !== void 0 ? _ref : false;
    const isFixedPrice = serviceCostModel.isFixedPrice(service.cost);
    const shouldDisableInStagesOption = (!isFixedPrice || hasValueDiscount) && !oneTimeInStages;
    const termsOptions = serviceBillingStagesTermsOptions({
        t: billingStagesTexts,
        now,
        agreementEffectiveDate: agreementData.effectiveDate,
        isFreshlyAddedToApprovedAgreement: isFromAgreement && !isExistingService
    });
    const isVariablePrice = serviceCostModel.isVariablePrice(service.cost);
    const occurrenceOptions = serviceBillingOccurrenceOptions({
        isVariablePrice,
        onDemandEnabled: !!ON_DEMAND,
        isCurrentlyOnDemand: isOnDemand,
        recurrencePeriodsTexts
    });
    const triggerOptions = serviceBillingTriggerOptions({
        t: billingTriggersTexts,
        recurrencePeriod: serviceBillingModel.recurrencePeriod(service.billing),
        pricingType: service.cost.pricingType,
        billingTrigger: service.billing.trigger,
        isFromAgreement,
        isExistingService,
        featureFlags,
        withEffectiveDate: {
            effectiveDate: agreementData.effectiveDate,
            now
        }
    });
    function handleOccurrenceChange(event) {
        const newOccurrence = event.target.value;
        const currentRecurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
        const isRecurring = !!currentRecurrencePeriod;
        if (newOccurrence === 'onetime') {
            if (isRecurring || oneTimeInStages || serviceBillingModel.isOnDemand(service.billing)) {
                serviceBillingModel.accept(service.billing, {
                    visitOneTimeManual: ()=>{},
                    visitOnApproval: ()=>{},
                    visitOneTimeInStages: ()=>{
                        onChange(serviceModel.setOneTimeManual(service));
                    },
                    visitOngoing: ()=>{
                        onChange(serviceModel.setOnApproval(service));
                    },
                    visitRepeatableManual: ()=>{
                        onChange(serviceModel.setOneTimeManual(service));
                    },
                    visitOnDemand: ()=>{
                        onChange(serviceModel.setOneTimeManual(service));
                    }
                });
            }
        } else if (newOccurrence === 'onDemand') {
            const existingMaxCharges = serviceBillingModel.maxCharges(service.billing);
            const existingPeriod = serviceBillingModel.preapprovalPeriod(service.billing);
            onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOnDemandBilling(existingPeriod !== null && existingPeriod !== void 0 ? existingPeriod : PreapprovalPeriodType.Monthly, {
                maxCharges: existingMaxCharges
            })));
        } else if (newOccurrence === 'inStages') {
            if (!isFixedPrice || hasValueDiscount) {
                return;
            }
            onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
        } else {
            const newRecurrencePeriod = newOccurrence;
            serviceBillingModel.accept(service.billing, {
                visitOneTimeManual: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
                },
                visitOnApproval: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
                        isAwaitingApproval: isAwaitingApproval(service.status)
                    })));
                },
                visitOneTimeInStages: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
                },
                visitOngoing: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
                        isAwaitingApproval: isAwaitingApproval(service.status),
                        billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
                        billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
                        billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
                        isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
                        maxCharges: serviceBillingModel.maxCharges(service.billing),
                        billUpfrontState: makeBillUpfrontState(serviceBillingModel.isSkipBillingOnAcceptance(service.billing))
                    })));
                },
                visitRepeatableManual: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
                        maxCharges: serviceBillingModel.maxCharges(service.billing)
                    })));
                },
                visitOnDemand: ()=>{
                    onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
                        maxCharges: serviceBillingModel.maxCharges(service.billing)
                    })));
                }
            });
        }
    }
    function handleTriggerChange(event) {
        const option = event.target.value;
        const setInStages = ()=>onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
        const setOnApproval = ()=>onChange(serviceModel.setOnApproval(service));
        const setOneTimeManual = ()=>onChange(serviceModel.setOneTimeManual(service));
        const doNothing = ()=>{};
        serviceBillingModel.accept(service.billing, {
            visitOneTimeManual: ()=>matchOption(option, {
                    manual: doNothing,
                    auto: setOnApproval,
                    inStages: setInStages
                }),
            visitOnApproval: ()=>matchOption(option, {
                    manual: setOneTimeManual,
                    auto: doNothing,
                    inStages: setInStages
                }),
            visitOneTimeInStages: ()=>matchOption(option, {
                    manual: setOneTimeManual,
                    auto: setOnApproval,
                    inStages: doNothing
                }),
            visitRepeatableManual: ()=>matchOption(option, {
                    manual: doNothing,
                    auto: ()=>onChange(serviceModel.setOngoing(service)),
                    inStages: doNothing
                }),
            visitOngoing: ()=>matchOption(option, {
                    manual: ()=>onChange(serviceModel.setRepeatableManual(service)),
                    auto: doNothing,
                    inStages: doNothing
                }),
            visitOnDemand: doNothing
        });
    }
    function handleTermsChange(event) {
        if (!oneTimeInStages) return;
        const terms = event.target.value;
        onChange(serviceModel.setOneTimeInStages(service, oneTimeInStages.setTerms(terms)));
    }
    const renderDropdowns = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Tooltip, {
                    placement: "top",
                    testId: BillingInitiationControlTestIds.Occurrence.Tooltip(),
                    title: disabledReason === 'existingServiceInAgreement' ? billingTriggersTexts.tooltips.cannotModifyOccurrence() : hasNonPercentageDiscountError ? billingInitiationTexts.tooltips.mustBePercentage() : '',
                    children: /*#__PURE__*/ _jsx(InlineSelect, {
                        bi: {
                            name: 'billing_occurrence'
                        },
                        skin: "primary",
                        value: occurrenceValue,
                        testId: BillingInitiationControlTestIds.Occurrence.Root(),
                        disabled: isDisabled,
                        error: hasNonPercentageDiscountError,
                        onChange: handleOccurrenceChange,
                        menuMaxHeight: 200,
                        SelectProps: {
                            renderValue: (value)=>getOccurrenceLabel(value, billingInitiationTexts, recurrencePeriodsTexts)
                        },
                        children: occurrenceOptions.map((param)=>{
                            let { value, text, disabled: optionDisabled } = param;
                            const isInStagesOption = value === 'inStages';
                            const isOnDemandOption = value === 'onDemand';
                            const isOptionDisabled = optionDisabled || isInStagesOption && shouldDisableInStagesOption;
                            const tooltipTitle = getOccurrenceOptionTooltip({
                                isInStagesOption,
                                isOnDemandOption,
                                isDisabled: isOptionDisabled,
                                hasValueDiscount,
                                isFixedPrice,
                                oneTimeInStages: !!oneTimeInStages,
                                t: billingInitiationTexts
                            });
                            return renderMenuItem({
                                value,
                                text,
                                testId: BillingInitiationControlTestIds.Occurrence.Option(value),
                                disabled: isOptionDisabled,
                                isMobileDevice,
                                tooltipProps: tooltipTitle ? {
                                    title: tooltipTitle,
                                    placement: 'top'
                                } : undefined
                            });
                        })
                    })
                }),
                !oneTimeInStages && /*#__PURE__*/ _jsx(Tooltip, {
                    placement: "top",
                    testId: BillingInitiationControlTestIds.Trigger.Tooltip(),
                    title: disabledReason === 'existingServiceInAgreement' ? billingTriggersTexts.tooltips.cannotModifyTrigger() : shouldShowYearlyManualTooltip ? billingTriggersTexts.tooltips.yearlyServicesManualOnly() : '',
                    children: /*#__PURE__*/ _jsx(InlineSelect, {
                        bi: {
                            name: 'billing_trigger'
                        },
                        skin: "primary",
                        value: triggerValue,
                        testId: BillingInitiationControlTestIds.Trigger.Root(),
                        disabled: isDisabled,
                        error: shouldShowYearlyManualTooltip,
                        onChange: handleTriggerChange,
                        menuMaxHeight: 500,
                        SelectProps: {
                            renderValue: (value)=>getTriggerLabel(value, billingInitiationTexts)
                        },
                        children: triggerOptions.map((param)=>{
                            let { value, text, subtext } = param;
                            const isAutomaticOption = value === 'approval' || value === 'ongoing';
                            const shouldDisableAutomaticOption = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && isAutomaticOption;
                            return renderMenuItem({
                                value,
                                text,
                                subtext,
                                testId: BillingInitiationControlTestIds.Trigger.Option(value),
                                disabled: shouldDisableAutomaticOption,
                                isMobileDevice,
                                tooltipProps: shouldDisableAutomaticOption && !isAutomaticOptionSelected ? {
                                    title: billingTriggersTexts.tooltips.yearlyServicesManualOnly(),
                                    placement: 'top'
                                } : undefined
                            });
                        })
                    })
                })
            ]
        });
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": BillingInitiationControlTestIds.Root(),
        children: [
            isReadOnly && /*#__PURE__*/ _jsx(Divider, {
                margin: 8
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsxs(TextAndInlineSelectRow, {
                    inline: true,
                    children: [
                        billingInitiationTexts.billed(),
                        " ",
                        renderDropdowns(),
                        oneTimeInStages && /*#__PURE__*/ _jsx(InlineSelect, {
                            bi: {
                                name: 'staged_billing_behaviour'
                            },
                            skin: "primary",
                            value: oneTimeInStages.terms(),
                            testId: BillingInitiationControlTestIds.Terms.Root(),
                            disabled: isDisabled,
                            onChange: handleTermsChange,
                            SelectProps: {
                                renderValue: (value)=>{
                                    const selected = termsOptions.find((opt)=>opt.value === value);
                                    return selected ? selected.text : '';
                                }
                            },
                            children: termsOptions.map((param)=>{
                                let { value, text, subtext } = param;
                                return renderMenuItem({
                                    value,
                                    text,
                                    subtext,
                                    testId: BillingInitiationControlTestIds.Terms.Option(value),
                                    isMobileDevice
                                });
                            })
                        }),
                        isOngoing && onBillUpfrontModeChange && /*#__PURE__*/ _jsx(InlineUpfrontSelect, {
                            service: service,
                            testId: testId,
                            disabled: isDisabled,
                            onChange: onBillUpfrontModeChange
                        })
                    ]
                })
            }),
            oneTimeInStages && /*#__PURE__*/ _jsx(BillingStagesSelect, {
                discount: ((_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.amount()) || new Decimal(0),
                totalRate: price(service.cost),
                effectiveDate: agreementData.effectiveDate,
                oneTimeInStagesBilling: oneTimeInStages,
                setOneTimeInStagesBilling: (oneTimeInStagesBilling)=>{
                    onChange(serviceModel.setOneTimeInStages(service, oneTimeInStagesBilling));
                },
                disabledReason: disabledReason,
                isAmendment: isFromAmendment(service)
            })
        ]
    });
}
